import { useEffect, useRef, useState } from 'react';
import { Account, InventoryAsset, Product } from '../../../types';
import {
  belongsToAccount,
  DISCONNECT_REQUEST,
  LOCATION_LIMIT,
  LocationScope,
  locationLabelsOf,
  MOVE_REQUEST,
  NEW_SERVICE_REQUEST
} from './serviceRequestModel';

interface LocationOptionsInput {
  location?: LocationScope;
  requestType: string;
  productSelection: Product | null;
  scopedInventory: InventoryAsset[];
  displayLocationCode: boolean;
}

/**
 * The Locations dropdown and the picks made in it: the options follow the scoped inventory (narrowed to
 * the chosen product for a Move), a pick that drops out of the options is pruned, and a form opened from
 * a location starts with that location's labels picked.
 */
export function useLocationOptions({
  location,
  requestType,
  productSelection,
  scopedInventory,
  displayLocationCode
}: LocationOptionsInput) {
  const [filteredLocations, setFilteredLocations] = useState<string[]>([]);
  const [locationSelections, setLocationSelections] = useState<string[]>([]);
  // The seed waits for the Locations options before the first pruning can judge it.
  const seedPending = useRef(false);

  // Single source of truth for the Locations dropdown: recompute whenever any input changes —
  // inventory (the shared store may already be warm before this form mounts, e.g. opening the
  // form on Home after Orders loaded it), the request type or selected product (only "Move
  // Existing Service" filters locations by product), or the location-code preference.
  useEffect(() => {
    const filterProduct = requestType === MOVE_REQUEST ? productSelection : null;
    const source = [NEW_SERVICE_REQUEST, DISCONNECT_REQUEST].includes(requestType)
      ? scopedInventory
      : scopedInventory.filter((item) => !filterProduct || filterProduct.id === item.product?.id);
    const locations = locationLabelsOf(source, displayLocationCode).sort();
    setFilteredLocations(locations);
    setLocationSelections((current) => {
      if (locations.length === 0 && seedPending.current) return current;
      seedPending.current = false;
      return current.filter((loc) => locations.includes(loc));
    });
  }, [scopedInventory, requestType, productSelection, displayLocationCode]);

  const seedFromLocation = (startingAccount: Account | null) => {
    if (!location) return;
    const seedRecords = location.installed.filter(
      (record) => !startingAccount || belongsToAccount(record, startingAccount)
    );
    setLocationSelections(locationLabelsOf(seedRecords, displayLocationCode).slice(0, LOCATION_LIMIT));
    seedPending.current = true;
  };

  const cancelSeed = () => {
    seedPending.current = false;
  };

  return { filteredLocations, locationSelections, setLocationSelections, seedFromLocation, cancelSeed };
}
